import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberAuth";
import { toast } from "sonner";
import { Package, Plus, Upload, ArrowsClockwise } from "@phosphor-icons/react";

const MODES = ["sea", "air", "road", "rail", "multimodal"];
const STATUSES = ["planned", "in_transit", "delivered", "delayed", "closed"];

const emptyForm = {
  reference: "",
  origin_country: "",
  origin_city: "",
  destination_country: "",
  destination_city: "",
  mode: "sea",
  cargo_type: "",
  volume_teu: "",
  weight_kg: "",
  counterparty_company: "",
  etd: "",
  eta: "",
  status: "planned",
  payment_protection_opt_in: false,
  notes: "",
};

export default function MemberShipments() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [importing, setImporting] = useState(false);

  const load = () => {
    setLoading(true);
    memberApi.get("/portal/shipments")
      .then(({ data }) => setShipments(data.shipments || []))
      .catch(() => toast.error("Could not load shipments"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reference || !form.origin_country || !form.destination_country) {
      toast.error("Reference, origin and destination country are required.");
      return;
    }
    const payload = {
      ...form,
      volume_teu: form.volume_teu ? parseFloat(form.volume_teu) : null,
      weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
    };
    try {
      await memberApi.post("/portal/shipments", payload);
      toast.success(`Shipment ${form.reference} logged.`);
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create shipment");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await memberApi.post("/portal/shipments/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} shipments${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="portal-shipments">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Portal</div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
            <Package size={24} weight="duotone" className="text-ember" /> Shipments
          </h1>
          <p className="text-sm text-navy/70 mt-2 max-w-xl">
            Log shipments you're planning or moving. Seeds the Member Match agent with real corridor data. Opt into
            Payment Protection per shipment when the counterparty is new or high-risk.
          </p>
        </div>
        <div className="flex gap-2">
          <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs uppercase tracking-widest border border-black/20 px-3 py-2 hover:bg-black/5" data-testid="shipments-import-btn">
            <Upload size={12} /> {importing ? "Importing…" : "Import CSV"}
            <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} data-testid="shipments-import-input" />
          </label>
          <button onClick={() => setShowForm(!showForm)} data-testid="shipments-new-btn"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-navy text-white px-3 py-2 hover:bg-navy/90">
            <Plus size={12} /> New shipment
          </button>
        </div>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} data-testid="shipments-form" className="border border-black/10 bg-white p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Reference *" testid="ship-reference">
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="input" data-testid="ship-reference-input" />
          </Field>
          <Field label="Mode" testid="ship-mode">
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="input" data-testid="ship-mode-input">
              {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Origin country *"><input value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} className="input" data-testid="ship-origin-country-input" /></Field>
          <Field label="Origin city"><input value={form.origin_city} onChange={(e) => setForm({ ...form, origin_city: e.target.value })} className="input" data-testid="ship-origin-city-input" /></Field>
          <Field label="Destination country *"><input value={form.destination_country} onChange={(e) => setForm({ ...form, destination_country: e.target.value })} className="input" data-testid="ship-destination-country-input" /></Field>
          <Field label="Destination city"><input value={form.destination_city} onChange={(e) => setForm({ ...form, destination_city: e.target.value })} className="input" data-testid="ship-destination-city-input" /></Field>
          <Field label="Cargo type"><input value={form.cargo_type} onChange={(e) => setForm({ ...form, cargo_type: e.target.value })} className="input" placeholder="e.g. Project cargo, DGR class 3" data-testid="ship-cargo-type-input" /></Field>
          <Field label="Counterparty company"><input value={form.counterparty_company} onChange={(e) => setForm({ ...form, counterparty_company: e.target.value })} className="input" data-testid="ship-counterparty-input" /></Field>
          <Field label="Volume (TEU)"><input value={form.volume_teu} onChange={(e) => setForm({ ...form, volume_teu: e.target.value })} className="input" type="number" step="0.1" data-testid="ship-volume-input" /></Field>
          <Field label="Weight (kg)"><input value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="input" type="number" step="0.1" data-testid="ship-weight-input" /></Field>
          <Field label="ETD"><input value={form.etd} onChange={(e) => setForm({ ...form, etd: e.target.value })} className="input" type="date" data-testid="ship-etd-input" /></Field>
          <Field label="ETA"><input value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="input" type="date" data-testid="ship-eta-input" /></Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input" data-testid="ship-status-input">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm text-navy/80 mt-6" data-testid="ship-pp-label">
            <input type="checkbox" checked={form.payment_protection_opt_in} onChange={(e) => setForm({ ...form, payment_protection_opt_in: e.target.checked })} data-testid="ship-pp-input" />
            Enrol in Payment Protection
          </label>
          <div className="md:col-span-2">
            <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input min-h-[70px]" /></Field>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy/90" data-testid="ship-submit">Log shipment</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); }} className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
          </div>
        </form>
      )}

      <section data-testid="shipments-list" className="border border-black/10 bg-white">
        <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between">
          <div className="overline">Your shipments ({shipments.length})</div>
          <button onClick={load} className="text-xs uppercase tracking-widest text-navy/60 hover:text-ember flex items-center gap-1" data-testid="shipments-refresh">
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-navy/50 text-sm">Loading…</div>
        ) : shipments.length === 0 ? (
          <div className="p-8 text-center text-navy/50 text-sm">No shipments logged yet. Add one above or import a CSV.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/5 text-navy/70 uppercase text-xs tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">Reference</th>
                  <th className="text-left px-4 py-3">Corridor</th>
                  <th className="text-left px-4 py-3">Mode</th>
                  <th className="text-left px-4 py-3">Cargo</th>
                  <th className="text-left px-4 py-3">ETD → ETA</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Protected</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((s) => (
                  <tr key={s.id} className="border-t border-black/5" data-testid={`ship-row-${s.reference}`}>
                    <td className="px-4 py-3 font-medium">{s.reference}</td>
                    <td className="px-4 py-3">{[s.origin_city, s.origin_country].filter(Boolean).join(", ")} → {[s.destination_city, s.destination_country].filter(Boolean).join(", ")}</td>
                    <td className="px-4 py-3 uppercase text-xs">{s.mode}</td>
                    <td className="px-4 py-3">{s.cargo_type || "—"}</td>
                    <td className="px-4 py-3 text-navy/70">{s.etd || "—"} → {s.eta || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-xs uppercase tracking-widest ${s.status === "delivered" ? "text-emerald-700" : s.status === "delayed" ? "text-amber-700" : "text-navy/70"}`}>{s.status}</span></td>
                    <td className="px-4 py-3">{s.payment_protection_opt_in ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="mt-6 text-xs text-navy/50">
        CSV columns: <code>reference,origin_country,origin_city,destination_country,destination_city,mode,cargo_type,volume_teu,weight_kg,counterparty_company,etd,eta,status,payment_protection_opt_in,notes</code>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block text-xs uppercase tracking-widest text-navy/60">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);
